import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, EntityService, SharedService } from '@app/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PriceDiscoveryService } from '../../../price-discovery/services/price-discovery.service';
import { PriceDiscoveryService as PurchaseManagerPriceDiscoveryService } from '../../services/price-discovery.service';
import { PurchaseManagerAnalyticsService } from '../../analytics';
import { HyperpureSupplierComponent } from '../hyperpure-supplier/hyperpure-supplier.component';
import { DmartSupplierComponent } from '../dmart-supplier/dmart-supplier.component';
import { UdaanSupplierComponent } from '../udaan-supplier/udaan-supplier.component';
import { DeliveritSupplierComponent } from '../deliverit-supplier/deliverit-supplier.component';

interface QuickAddCategory {
    label: string;
    icon: string;
    key: string;
    active: boolean;
    count?: number;
    addedTags?: string[];
}

interface SearchTag {
    id: string;
    name: string;
}

interface ProductCard {
    id: string;
    name: string;
    packageInfo: string;
    priceLabel: string;
    minPrice: number;
    maxPrice: number;
    imageUrl: string;
    supplier?: string;
    unit?: string;
    quantity?: number;
}

@Component({
    selector: 'app-pm-landing',
    templateUrl: './pm-landing.component.html',
    styleUrls: ['./pm-landing.component.scss']
})
export class PmLandingComponent implements OnInit, AfterViewInit, OnDestroy {

    // Entity
    selectedEntityId = '';
    entities: any[] = [];

    // Search
    searchQuery = '';
    searchTags: SearchTag[] = [];
    searchResults: any[] = [];
    isSearching = false;
    showSearchDropdown = false;
    searchDebounceTimer: any = null;

    // Quick Add
    quickAddCategories: QuickAddCategory[] = [
        // { label: 'Most Purchased', icon: 'shopping_cart', key: 'mostPurchased', active: false },
        // { label: 'Recent Purchased', icon: 'schedule', key: 'recentPurchase', active: false },
        { label: 'Recent Searched', icon: 'history', key: 'recentSearched', active: false },
        { label: 'Most Searched', icon: 'search', key: 'mostSearched', active: false },
        { label: 'Trending Near You', icon: 'trending_up', key: 'trendingNearYou', active: false },
        { label: 'Recent Purchases', icon: 'local_shipping', key: 'hpRecentPurchases', active: false },
        // { label: 'Top 10 Ordered', icon: 'trending_up', key: 'topOrdered', active: false }
    ];

    // Product cards
    recentPurchases: ProductCard[] = [];
    mostSearchedItems: ProductCard[] = [];
    trendingItems: ProductCard[] = [];
    isLoading = false;

    // Cart state from APIs
    isHyperpureEnabled = false;
    isDmartEnabled = false;
    isUdaanEnabled = false;
    isDeliveritEnabled = false;
    deliveritStoreData: any = null;
    hyperpureStoreUuid = '';
    dmartStoreId = '';
    udaanApiStoreId = '';
    deliverItStoreId = '';
    supplierCartData: { platform: string; supplierName: string; totalValue: number; totalCount: number }[] = [];
    isCartLoading = false;
    private isViewInitialized = false;

    // Favorites
    favoriteItems: Set<string> = new Set();
    favoriteItemsList: any[] = [];
    showFavoritesModal = false;
    isFavoritesLoading = false;
    searchFavoriteTerm = '';

    // HP Recent Purchases
    hpRecentPurchaseLiveItems: any[] = [];
    hpRecentPurchaseMissingItems: any[] = [];
    showHpRecentPurchasesModal = false;
    isHpRecentPurchasesLoading = false;
    searchHpRecentPurchaseTerm = '';
    hpRecentPurchaseTotalCount = 0;
    hpRecentPurchaseLiveCount = 0;
    hpRecentPurchaseMissingCount = 0;
    showAllTags = false;

    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
    @ViewChild('cartsContainer') cartsContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('hyperpureSupplier') hyperpureSupplier!: HyperpureSupplierComponent;
    @ViewChild('dmartSupplier') dmartSupplier!: DmartSupplierComponent;
    @ViewChild('udaanSupplier') udaanSupplier!: UdaanSupplierComponent;
    @ViewChild('deliveritSupplier') deliveritSupplier!: DeliveritSupplierComponent;

    private destroy$ = new Subject<void>();
    private entityChangeSubscription: Subscription;

    @HostListener('document:keydown', ['$event'])
    handleKeyboardShortcut(event: KeyboardEvent): void {
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
            event.preventDefault();
            this.searchInput?.nativeElement?.focus();
        }
        // Ctrl+Enter / Cmd+Enter to trigger search
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            event.preventDefault();
            this.onSearchNavigate();
        }
    }

    constructor(
        private priceDiscoveryService: PriceDiscoveryService,
        private pmPriceDiscoveryService: PurchaseManagerPriceDiscoveryService,
        private entityService: EntityService,
        private authService: AuthService,
        private sharedService: SharedService,
        private router: Router,
        private pmAnalytics: PurchaseManagerAnalyticsService
    ) { }

    /**
     * Initializes the component by listening to entity changes and loading initial entities.
     */
    ngOnInit(): void {
        this.listenToEntityChanges();
        this.loadEntities();

        // Initialize analytics
        const user = this.authService.user;
        const entity = this.entityService.getSingleSelectedEntity();
        this.pmAnalytics.initialize({
            userId: user?.id || '',
            userName: user?.name || '',
            userEmail: user?.email || '',
            entityId: this.selectedEntityId,
            companyId: user?.company?.id || entity?.parent || entity?.parentId || '',
            companyName: user?.company?.name || entity?.group || '',
        });
        this.pmAnalytics.trackPageView('/purchase-manager', 'Landing');
    }

    ngAfterViewInit(): void {
        this.isViewInitialized = true;
        // If loadCartData already ran before view was ready, initialize Hyperpure now
        if (this.isHyperpureEnabled && this.hyperpureStoreUuid && this.hyperpureSupplier) {
            this.hyperpureSupplier.initialize();
        }
        if (this.isDeliveritEnabled && this.deliverItStoreId && this.deliveritSupplier) {
            this.deliveritSupplier.initialize();
        }
    }

    /**
     * Cleans up subscriptions and timers when the component is destroyed.
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.entityChangeSubscription?.unsubscribe();
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
    }

    // ── Entity Management ──

    /**
     * Subscribes to entity selection changes to reload page data when a different entity is selected.
     */
    listenToEntityChanges(): void {
        this.entityChangeSubscription = this.entityService.singleSelectedEntity$.subscribe(selectedEntity => {
            if (selectedEntity && selectedEntity.id) {
                if (this.selectedEntityId && this.selectedEntityId !== selectedEntity.id) {
                    this.selectedEntityId = selectedEntity.id;
                    this.loadPageData();
                }
            }
        });
    }

    /**
     * Loads entities from the entity service and sets the selected entity ID.
     */
    loadEntities(): void {
        const storedEntities = this.entityService.getEntities();
        const selectedEntity = this.entityService.getSingleSelectedEntity();

        if (storedEntities && storedEntities.length > 0) {
            this.entities = storedEntities.map((entity: any) => ({
                id: entity.id,
                name: entity.name
            }));
        }

        if (selectedEntity && !this.selectedEntityId) {
            this.selectedEntityId = selectedEntity.id;
            this.loadPageData();
            this.loadCartData();
        }
    }

    /**
     * Handles entity change event from the UI, updates the selected entity in the service, and reloads page data.
     */
    onEntityChange(): void {
        const entity = this.entities.find(e => e.id === this.selectedEntityId);
        if (entity) {
            this.entityService.setSingleSelectedEntity(entity);
        }
        this.loadPageData();
    }

    // ── Page Data Loading ──

    /**
     * Loads all data required for the landing page for the selected entity.
     */
    loadPageData(): void {
        if (!this.selectedEntityId) return;

        this.isLoading = true;
        this.loadRecentPurchases();
        this.loadTrendingItems();
        this.loadFavoriteItems();
        this.loadCartData();
    }

    /**
     * Fetches recent searched items from the fast-tags API for the last 30 days.
     */
    loadRecentPurchases(): void {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);

        this.priceDiscoveryService.getRecentSearchedItems(
            { start_date: startDate.toISOString(), end_date: endDate.toISOString(), date_field: 'invoiceDate' },
            this.selectedEntityId
        ).pipe(takeUntil(this.destroy$))
            .subscribe(
                (items: any[]) => {
                    this.recentPurchases = this.mapRecentSearchedItems(items);
                    this.isLoading = false;
                },
                (error: any) => {
                    console.error('Failed to load recent searched items', error);
                    this.isLoading = false;
                }
            );
    }

    /**
     * Fetches most searched items from the API for the last 30 days.
     */
    loadMostSearchedItems(): void {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);

        this.isLoading = true;
        this.priceDiscoveryService.getMostSearchedItems(
            { startDate: startDate.toISOString(), endDate: endDate.toISOString(), dateField: 'invoiceDate' },
            this.selectedEntityId
        ).pipe(takeUntil(this.destroy$))
            .subscribe(
                (items: any[]) => {
                    this.mostSearchedItems = this.mapRecentSearchedItems(items);
                    this.isLoading = false;
                },
                (error: any) => {
                    console.error('Failed to load most searched items', error);
                    this.isLoading = false;
                }
            );
    }

    /**
     * Fetches trending near you items from the fast-tags API for the last 30 days.
     */
    loadTrendingItems(): void {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);

        this.priceDiscoveryService.getTrendingNearYouItems(
            { start_date: startDate.toISOString(), end_date: endDate.toISOString(), date_field: 'invoiceDate' },
            this.selectedEntityId
        ).pipe(takeUntil(this.destroy$))
            .subscribe(
                (items: any[]) => {
                    this.trendingItems = this.mapRecentSearchedItems(items);
                },
                (error: any) => {
                    console.error('Failed to load trending items', error);
                }
            );
    }

    /**
     * Fetches the list of favorite items for the selected entity.
     */
    loadFavoriteItems(): void {
        this.priceDiscoveryService.getFavoriteItems(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    const items = response?.items || response || [];
                    this.favoriteItems = new Set(items.map((item: any) => item.item_name || item.itemName));
                },
                (error: any) => {
                    console.error('Failed to load favorites', error);
                }
            );
    }

    /**
     * Loads cart data for all enabled supplier integrations.
     * Calls checkIntegrationStatus first, then fetches cart for each enabled supplier.
     */
    loadCartData(): void {
        if (!this.selectedEntityId) return;

        this.isCartLoading = true;
        this.supplierCartData = [];

        this.priceDiscoveryService.checkIntegrationStatus(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.isHyperpureEnabled = response?.isHyperpureEnabled || false;
                    this.isDmartEnabled = response?.isDmartEnabled || false;
                    this.isUdaanEnabled = response?.isUdaanEnabled || false;
                    this.isDeliveritEnabled = response?.isDeliveritEnabled || false;

                    if (this.isHyperpureEnabled) {
                        this.hyperpureStoreUuid = response?.hyperpureStoreUuid || '';
                        if (this.hyperpureStoreUuid) {
                            this.fetchHyperpureCartData();
                            if (this.isViewInitialized && this.hyperpureSupplier) {
                                this.hyperpureSupplier.initialize();
                            }
                        }
                    }

                    if (this.isDmartEnabled) {
                        this.dmartStoreId = response?.dmartStoreData?.storeId || response?.dmartId || '';
                        if (this.dmartStoreId) {
                            this.fetchDmartCartData();
                        }
                    }

                    if (this.isUdaanEnabled) {
                        this.udaanApiStoreId = response?.udaanStoreData?.udaanResponse?.stores?.[0]?.storeId ||
                            response?.udaanStoreData?.udaanResponse?.stores?.[0]?.store_id ||
                            response?.udaanStoreData?.storeId || '';
                        if (this.udaanApiStoreId) {
                            this.fetchUdaanCartData();
                        }
                    }

                    if (this.isDeliveritEnabled) {
                        const deliveritStoreUuid = response?.deliveritStoreUuid || response?.deliveritStoreData?.storeUuid || response?.deliveritStoreData?.store_uuid || '';

                        const requestPayload = {
                            storeUuid: deliveritStoreUuid
                        };

                        this.priceDiscoveryService.deliveritSignIn(this.selectedEntityId, requestPayload)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe(
                                (signInResponse: any) => {
                                    console.log('✅ DeliverIt sign-in successful:', signInResponse);
                                    const storeData = signInResponse?.response || signInResponse;
                                    this.deliveritStoreData = storeData;
                                    this.deliverItStoreId = storeData?.storeId || storeData?.store_id || '';
                                    
                                    if (this.deliverItStoreId) {
                                        this.fetchDeliverItCartData();
                                        if (this.isViewInitialized && this.deliveritSupplier) {
                                            this.deliveritSupplier.storeData = this.deliveritStoreData;
                                            this.deliveritSupplier.initialize();
                                        }
                                    }
                                },
                                (error: any) => {
                                    console.error('Failed to sign-in to DeliverIt:', error);
                                    // Fallback to existing data if sign-in fails
                                    this.deliverItStoreId = response?.deliveritStoreUuid || response?.deliveritStoreData?.storeId || response?.deliveritStoreData?.storeUuid || '';
                                    this.deliveritStoreData = response?.deliveritStoreData || null;
                                    if (this.deliverItStoreId) {
                                        this.fetchDeliverItCartData();
                                        if (this.isViewInitialized && this.deliveritSupplier) {
                                            this.deliveritSupplier.initialize();
                                        }
                                    }
                                }
                            );
                    }

                    this.isCartLoading = false;
                },
                (error: any) => {
                    console.error('Failed to check integration status for cart', error);
                    this.isCartLoading = false;
                }
            );
    }

    private fetchHyperpureCartData(): void {
        this.priceDiscoveryService.getCart(this.hyperpureStoreUuid, 'DELIVERY', this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    const cartProducts = response?.response?.cartProducts || response?.response?.cart_products || [];
                    const cartCount = response?.response?.cartCount || response?.response?.cart_count || cartProducts.length;
                    if (cartCount > 0) {
                        const totalValue = cartProducts.reduce((sum: number, cp: any) => {
                            const pd = cp.product_details || cp.productDetails || {};
                            const price = Number(pd.price_per_unit || pd.pricePerUnit || pd.discounted_price_per_unit || pd.discountedPricePerUnit || 0);
                            return sum + price * (cp.quantity || 1);
                        }, 0);
                        this.upsertSupplierCart('hyperpure', 'Hyperpure', totalValue, cartCount);
                    }
                },
                (error: any) => { console.error('Failed to fetch Hyperpure cart', error); }
            );
    }

    private fetchDmartCartData(): void {
        this.pmPriceDiscoveryService.getDmartCart(this.dmartStoreId, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    const data = response?.response || response?.data || response || {};
                    const products = data.products || [];
                    const cartCount = Number(data.cartTotalItem || 0) || products.length;
                    if (cartCount > 0) {
                        let totalValue = 0;
                        products.forEach((product: any) => {
                            const skus = product.sKUs || product.skus || product.SKUs || [];
                            skus.forEach((sku: any) => {
                                const price = Number(sku.priceSALE || sku.priceSale || sku.price || 0);
                                const qty = Number(sku.addedQuantity || sku.quantity || 1);
                                totalValue += price * qty;
                            });
                        });
                        this.upsertSupplierCart('dmart', 'DMart', totalValue, cartCount);
                    }
                },
                (error: any) => { console.error('Failed to fetch DMart cart', error); }
            );
    }

    private fetchUdaanCartData(): void {
        this.priceDiscoveryService.getUdaanCart(this.udaanApiStoreId, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    const data = response?.response || response;
                    const products: any[] = data?.products || [];
                    const cartCount = data?.cartCount ?? data?.cart_count ?? products.length;
                    if (cartCount > 0) {
                        const totalValue = products.reduce((sum: number, p: any) => {
                            const price = Number(p.discountedPricePerUnit || p.discounted_price_per_unit || p.pricePerUnit || p.price_per_unit || 0);
                            const qty = Number(p.productQuantity || p.product_quantity || 1);
                            return sum + price * qty;
                        }, 0);
                        this.upsertSupplierCart('udaan', 'Udaan', totalValue, cartCount);
                    }
                },
                (error: any) => { console.error('Failed to fetch Udaan cart', error); }
            );
    }

    private fetchDeliverItCartData(): void {
        this.priceDiscoveryService.getDeliverItCart(this.deliverItStoreId, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    const data = response?.response || response;
                    let totalValue = 0;
                    let cartCount = data?.cart_count ?? data?.cartCount ?? 0;

                    if (data?.cart_products && Array.isArray(data.cart_products)) {
                        totalValue = data.cart_products.reduce((sum: number, cp: any) => {
                            const p = cp.product_details || {};
                            const qty = cp.quantity || 0;
                            const price = Number(p.discounted_price_per_unit || p.discountedPricePerUnit || p.price_per_unit || p.pricePerUnit || 0);
                            return sum + price * qty;
                        }, 0);
                        if (cartCount === 0) {
                            cartCount = data.cart_products.reduce((sum: number, cp: any) => sum + (cp.quantity || 0), 0);
                        }
                    } else {
                        const itemsList = data?.items || data?.products || [];
                        totalValue = itemsList.reduce((sum: number, p: any) => {
                            const price = Number(p.price || p.discountedPricePerUnit || p.discounted_price_per_unit || p.pricePerUnit || p.price_per_unit || 0);
                            const qty = Number(p.quantity || p.productQuantity || p.product_quantity || 0);
                            return sum + price * qty;
                        }, 0);
                        if (cartCount === 0) {
                            cartCount = itemsList.reduce((sum: number, p: any) => sum + Number(p.quantity || p.productQuantity || p.product_quantity || 0), 0);
                        }
                    }

                    if (cartCount > 0) {
                        this.upsertSupplierCart('deliverit', 'DeliverIt', totalValue, cartCount);
                    }
                },
                (error: any) => { console.error('Failed to fetch DeliverIt cart', error); }
            );
    }

    private upsertSupplierCart(platform: string, supplierName: string, totalValue: number, totalCount: number): void {
        const idx = this.supplierCartData.findIndex(c => c.platform === platform);
        const cart = { platform, supplierName, totalValue, totalCount };
        if (idx >= 0) {
            this.supplierCartData[idx] = cart;
        } else {
            this.supplierCartData.push(cart);
        }
    }

    get totalCartValue(): number {
        return this.supplierCartData.reduce((sum, c) => sum + c.totalValue, 0);
    }

    get hasCartItems(): boolean {
        return this.supplierCartData.length > 0;
    }

    /**
     * Navigate to price discovery page, optionally focusing on a specific supplier.
     */
    scrollCarts(direction: 'left' | 'right'): void {
        const el = this.cartsContainer?.nativeElement;
        if (el) {
            el.scrollBy({ left: direction === 'right' ? 280 : -280, behavior: 'smooth' });
        }
    }

    proceedToBuy(platform: string): void {
        switch (platform) {
            case 'hyperpure':
                if (this.hyperpureSupplier) {
                    this.hyperpureSupplier.proceedToBuy();
                }
                break;
            case 'dmart':
                window.open('https://www.dmart.in/cart', '_blank');
                this.sharedService.successAlert('Opening DMart website...');
                break;
            case 'udaan':
                window.open('https://udaan.com/boltCart/?utm_source=petpooja', '_blank');
                this.sharedService.successAlert('Redirecting to Udaan...');
                break;
            case 'deliverit':
                if (this.deliveritSupplier) {
                    this.deliveritSupplier.proceedToBuy();
                }
                break;
            default:
                this.router.navigate(['/purchase-manager/price-discovery']);
        }
    }

    onIframeClosed(): void {
        this.loadCartData();
    }

    goToPriceDiscovery(platform?: string): void {
        this.router.navigate(['/purchase-manager/price-discovery']);
    }

    // ── Search ──

    /**
     * Handles search input events, implementing debounce logic to avoid excessive API calls.
     */
    onSearchInput(): void {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }

        if (!this.searchQuery || this.searchQuery.length < 2) {
            this.showSearchDropdown = false;
            this.searchResults = [];
            return;
        }

        this.searchDebounceTimer = setTimeout(() => {
            this.performSearch();
        }, 300);
    }

    /**
     * Performs the actual search API call based on the current search query.
     */
    performSearch(): void {
        if (!this.searchQuery || !this.selectedEntityId) return;

        this.isSearching = true;
        this.showSearchDropdown = true;
        const searchStartTime = Date.now();

        this.priceDiscoveryService.searchProducts(this.searchQuery, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (results: any[]) => {
                    this.searchResults = results || [];
                    this.isSearching = false;

                    // Track search
                    this.pmAnalytics.trackSearch('all', {
                        keyword: this.searchQuery,
                        resultCount: this.searchResults.length,
                        searchSource: 'typed',
                        responseTimeMs: Date.now() - searchStartTime,
                    });
                },
                (error: any) => {
                    console.error('Search failed', error);
                    this.searchResults = [];
                    this.isSearching = false;
                }
            );
    }

    /**
     * Adds a product from search results as a search tag.
     * @param item The product item to add as a tag.
     */
    addSearchTag(item: any): void {
        const tagName = item.itemName || item.item_name || item.name;
        if (!this.searchTags.find(t => t.name === tagName)) {
            this.searchTags.push({
                id: item.id || item._id || tagName,
                name: tagName
            });
        }

        // Track item selection from search results
        this.pmAnalytics.trackSearchItemSelect('all', {
            keyword: this.searchQuery || tagName,
            itemName: tagName,
            itemId: item.id || item._id || '',
            itemPrice: item.rate || item.price || 0,
            itemUnit: item.unit || '',
            resultPosition: this.searchResults.indexOf(item),
            isBestPrice: false,
            addedToCart: false,
        });

        this.searchQuery = '';
        this.showSearchDropdown = false;
    }

    /**
     * Removes a search tag from the active search tags.
     * @param tag The tag to remove.
     */
    removeSearchTag(tag: SearchTag): void {
        this.searchTags = this.searchTags.filter(t => t.id !== tag.id);

        // Clean up quick add category tracking when a tag is manually removed
        this.quickAddCategories.forEach(cat => {
            if (cat.addedTags) {
                cat.addedTags = cat.addedTags.filter(n => n.toLowerCase() !== tag.name.toLowerCase());
                cat.count = cat.addedTags.length;
                if (cat.addedTags.length === 0) {
                    cat.active = false;
                }
            }
        });
    }

    /**
     * Handles search form submission — adds the current query as a tag and navigates to price discovery.
     */
    onSearchSubmit(): void {
        if (this.searchQuery && this.searchQuery.trim().length >= 2) {
            // Add current search query as tag and navigate
            this.onSearchNavigate();
        } else if (this.searchTags.length > 0) {
            // No new text but existing tags — navigate with them
            this.onSearchNavigate();
        }
    }

    /**
     * Finalizes search tags and navigates to the price discovery page with the selected tags.
     */
    onSearchNavigate(): void {
        // If there's text in the input, add it as a tag first
        if (this.searchQuery && this.searchQuery.trim().length >= 2) {
            const tagName = this.searchQuery.trim();
            if (!this.searchTags.find(t => t.name.toLowerCase() === tagName.toLowerCase())) {
                this.searchTags.push({
                    id: 'manual_' + Date.now(),
                    name: tagName
                });
            }
            this.searchQuery = '';
        }

        // Navigate to price discovery with search tags
        if (this.searchTags.length > 0) {
            const tags = this.searchTags.map(t => t.name).join('$$,$$');
            this.router.navigate(['/purchase-manager/price-discovery'], {
                queryParams: { tags: this.sharedService.encryptForUrl(tags) }
            });
        }
    }

    /**
     * Handles search input blur event to hide the search dropdown.
     */
    onSearchBlur(): void {
        // Delay closing to allow click events on dropdown items
        setTimeout(() => {
            this.showSearchDropdown = false;
        }, 200);
    }

    // ── Quick Add ──

    /**
     * Handles clicks on quick add categories, toggling their active state and loading relevant items.
     * @param category The quick add category clicked.
     */
    onQuickAddClick(category: QuickAddCategory): void {
        // If active, toggle off and remove its tags
        if (category.active) {
            this.dismissQuickAdd(category);
            return;
        }

        category.active = true;
        this.isLoading = true;

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);

        let observable;
        switch (category.key) {
            case 'recentSearched': {
                const fastTagParams = {
                    start_date: startDate.toISOString(),
                    end_date: endDate.toISOString(),
                    date_field: 'invoiceDate'
                };
                observable = this.priceDiscoveryService.getRecentSearchedItems(fastTagParams, this.selectedEntityId);
                break;
            }
            case 'trendingNearYou': {
                const fastTagParams = {
                    start_date: startDate.toISOString(),
                    end_date: endDate.toISOString(),
                    date_field: 'invoiceDate'
                };
                observable = this.priceDiscoveryService.getTrendingNearYouItems(fastTagParams, this.selectedEntityId);
                break;
            }
            case 'mostSearched':
                observable = this.priceDiscoveryService.getMostSearchedItems(
                    { startDate: startDate.toISOString(), endDate: endDate.toISOString(), dateField: 'invoiceDate' },
                    this.selectedEntityId
                );
                break;
            case 'mostPurchased':
                observable = this.priceDiscoveryService.getMostPurchasedItems(
                    { startDate: startDate.toISOString(), endDate: endDate.toISOString(), dateField: 'invoiceDate' },
                    this.selectedEntityId
                );
                break;
            case 'recentPurchase':
                observable = this.priceDiscoveryService.getRecentPurchaseItems(
                    { startDate: startDate.toISOString(), endDate: endDate.toISOString(), dateField: 'invoiceDate' },
                    this.selectedEntityId
                );
                break;
            case 'topOrdered':
                observable = this.priceDiscoveryService.getTopOrderedItems(
                    { startDate: startDate.toISOString(), endDate: endDate.toISOString(), dateField: 'invoiceDate' },
                    this.selectedEntityId
                );
                break;
            case 'hpRecentPurchases': {
                // HP Recent Purchases returns { live_items, missing_items }
                // We extract item names from live_items for search tags
                this.pmPriceDiscoveryService.getHpRecentPurchases({ days: 60 }, this.selectedEntityId)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(
                        (response: any) => {
                            const liveItems = response?.liveItems || [];
                            const addedNames: string[] = [];
                            liveItems.slice(0, 10).forEach((item: any) => {
                                const name = item.itemName || item.itemName || item.name;
                                if (name && !this.searchTags.find(t => t.name.toLowerCase() === name.toLowerCase())) {
                                    this.searchTags.push({
                                        id: item.productId?.toString() || item.productId?.toString() || name,
                                        name: name
                                    });
                                    addedNames.push(name);
                                }
                            });
                            category.addedTags = addedNames;
                            category.count = addedNames.length;
                            this.isLoading = false;
                            if (addedNames.length === 0) {
                                category.active = false;
                            }
                        },
                        (error: any) => {
                            console.error('Failed to load Recent Purchases', error);
                            this.isLoading = false;
                            category.active = false;
                        }
                    );
                return; // Early return since we handle subscription directly
            }
            default:
                this.isLoading = false;
                category.active = false;
                return;
        }

        observable.pipe(takeUntil(this.destroy$)).subscribe(
            (items: any[]) => {
                const mapped = (items || []).slice(0, 10);
                const addedNames: string[] = [];
                mapped.forEach((item: any) => {
                    const name = item.itemName || item.item_name || item.name;
                    if (name && !this.searchTags.find(t => t.name.toLowerCase() === name.toLowerCase())) {
                        this.searchTags.push({
                            id: item.id || item._id || name,
                            name: name
                        });
                        addedNames.push(name);
                    }
                });

                category.addedTags = addedNames;
                category.count = addedNames.length;
                this.isLoading = false;

                if (addedNames.length === 0) {
                    category.active = false;
                }

                this.pmAnalytics.trackQuickAddCategoryView(
                    category.key as any,
                    mapped.length
                );
            },
            (error: any) => {
                console.error(`Failed to load ${category.key}`, error);
                this.isLoading = false;
                category.active = false;
            }
        );
    }

    /**
     * Dismisses an active quick-add category and clears its associated data.
     */
    dismissQuickAdd(category: QuickAddCategory): void {
        category.active = false;

        // Remove search tags that were added by this category
        if (category.addedTags && category.addedTags.length > 0) {
            const tagsToRemove = new Set(category.addedTags.map(n => n.toLowerCase()));
            this.searchTags = this.searchTags.filter(t => !tagsToRemove.has(t.name.toLowerCase()));
        }
        category.addedTags = [];
        category.count = 0;
    }

    // ── Favorites ──

    /**
     * Toggles the visibility of the favorites modal and loads favorite items if opening.
     */
    toggleFavoritesModal(): void {
        this.showFavoritesModal = !this.showFavoritesModal;
        if (this.showFavoritesModal) {
            this.searchFavoriteTerm = '';
            this.loadFavoriteItemsList();
        }
    }

    /**
     * Loads the detailed list of favorite items for display in the modal.
     */
    loadFavoriteItemsList(): void {
        this.isFavoritesLoading = true;
        this.priceDiscoveryService.getFavoriteItems(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.favoriteItemsList = response?.items || response || [];
                    this.isFavoritesLoading = false;
                },
                (error: any) => {
                    console.error('Failed to load favorites list', error);
                    this.favoriteItemsList = [];
                    this.isFavoritesLoading = false;
                }
            );
    }

    /**
     * Returns a filtered list of favorite items based on the favorite search term.
     */
    get filteredFavorites(): any[] {
        if (!this.searchFavoriteTerm) return this.favoriteItemsList;
        const term = this.searchFavoriteTerm.toLowerCase();
        return this.favoriteItemsList.filter((item: any) => {
            const name = item.item_name || item.itemName || item.name || '';
            return name.toLowerCase().includes(term);
        });
    }

    /**
     * Checks if an item is currently marked as a favorite.
     * @param itemName Name of the item to check.
     * @returns True if it's a favorite, false otherwise.
     */
    isFavorite(itemName: string): boolean {
        return this.favoriteItems.has(itemName);
    }

    /**
     * Toggles the favorite status of a product.
     * @param item The product to toggle favorite status for.
     */
    toggleFavorite(item: ProductCard): void {
        if (this.favoriteItems.has(item.name)) {
            this.priceDiscoveryService.removeFavoriteItem(this.selectedEntityId, item.name)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.favoriteItems.delete(item.name);
                    this.favoriteItemsList = this.favoriteItemsList.filter(
                        (f: any) => (f.item_name || f.itemName || f.name) !== item.name
                    );
                });
        } else {
            this.priceDiscoveryService.addFavoriteItem(this.selectedEntityId, item.name)
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.favoriteItems.add(item.name);
                    this.favoriteItemsList.push({ item_name: item.name });
                });
        }
    }

    /**
     * Removes an item from favorites by its name.
     * @param itemName Name of the item to remove from favorites.
     */
    removeFavoriteByName(itemName: string): void {
        this.priceDiscoveryService.removeFavoriteItem(this.selectedEntityId, itemName)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.favoriteItems.delete(itemName);
                this.favoriteItemsList = this.favoriteItemsList.filter(
                    (f: any) => (f.item_name || f.itemName || f.name) !== itemName
                );
            });
    }

    /**
     * Navigates to price discovery for a specific favorite item.
     * @param favItem The favorite item to search for.
     */
    // ── HP Recent Purchases ──

    /**
     * Toggles the visibility of the HP Recent Purchases modal and loads items if opening.
     */
    toggleHpRecentPurchasesModal(): void {
        this.showHpRecentPurchasesModal = !this.showHpRecentPurchasesModal;
        if (this.showHpRecentPurchasesModal) {
            this.searchHpRecentPurchaseTerm = '';
            this.loadHpRecentPurchaseItems();
        }
    }

    /**
     * Loads HP Recent Purchases items (live + missing) for the modal.
     */
    loadHpRecentPurchaseItems(): void {
        this.isHpRecentPurchasesLoading = true;
        this.pmPriceDiscoveryService.getHpRecentPurchases({ days: 60 }, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.hpRecentPurchaseLiveItems = response?.liveItems || [];
                    this.hpRecentPurchaseMissingItems = response?.missingItems || [];
                    this.hpRecentPurchaseTotalCount = response?.totalCount || 0;
                    this.hpRecentPurchaseLiveCount = response?.liveCount || 0;
                    this.hpRecentPurchaseMissingCount = response?.missingCount || 0;
                    this.syncHpRecentPurchaseQuantities();
                    this.isHpRecentPurchasesLoading = false;
                },
                (error: any) => {
                    console.error('Failed to load Recent Purchases', error);
                    this.hpRecentPurchaseLiveItems = [];
                    this.hpRecentPurchaseMissingItems = [];
                    this.isHpRecentPurchasesLoading = false;
                }
            );
    }

    /**
     * Returns filtered live items based on search term.
     */
    get filteredHpLiveItems(): any[] {
        if (!this.searchHpRecentPurchaseTerm) return this.hpRecentPurchaseLiveItems;
        const term = this.searchHpRecentPurchaseTerm.toLowerCase();
        return this.hpRecentPurchaseLiveItems.filter((item: any) => {
            const name = item.itemName || item.item_name || item.name || '';
            return name.toLowerCase().includes(term);
        });
    }

    /**
     * Returns filtered missing items based on search term.
     */
    get filteredHpMissingItems(): any[] {
        if (!this.searchHpRecentPurchaseTerm) return this.hpRecentPurchaseMissingItems;
        const term = this.searchHpRecentPurchaseTerm.toLowerCase();
        return this.hpRecentPurchaseMissingItems.filter((item: any) => {
            const name = item.itemName || item.item_name || item.name || '';
            return name.toLowerCase().includes(term);
        });
    }

    /**
     * Navigates to price discovery for a specific HP recent purchase item.
     */
    searchHpRecentPurchaseItem(item: any): void {
        const name = item.item_name || item.itemName || item.name;
        if (name) {
            this.showHpRecentPurchasesModal = false;
            this.router.navigate(['/purchase-manager/price-discovery'], {
                queryParams: { tags: this.sharedService.encryptForUrl(name) }
            });
        }
    }

    /**
     * Adds an HP recent purchase item to search tags.
     */
    addHpItemToSearch(item: any): void {
        const name = item.item_name || item.itemName || item.name;
        if (name && !this.searchTags.find(t => t.name.toLowerCase() === name.toLowerCase())) {
            this.searchTags.push({
                id: item.sku_id?.toString() || item.product_number?.toString() || name,
                name: name
            });
        }
    }

    /**
     * Checks if an HP recent purchase item is already in search tags.
     */
    isHpItemAdded(item: any): boolean {
        const name = (item.itemName || item.itemName || item.name || '').toLowerCase();
        return this.searchTags.some(t => t.name.toLowerCase() === name);
    }

    /**
     * Adds an HP item to the Hyperpure cart.
     */
    addHpToCart(item: any): void {
        if (item.isAddingToCart) return;

        if (item.rate) {
            const previousQuantity = item.selectedQuantity || 0;
            
            // Setup item properties required by HyperpureSupplierComponent
            item.sku_id = item.productId || item.product_id || item.sku_id || item.id;
            item.item_name = item.itemName || item.item_name || item.name;
            item.platform = 'hyperpure';

            if (item.maximumOrderQty && (previousQuantity + 1) > item.maximumOrderQty) {
                this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
                return;
            }

            if (previousQuantity === 0 && item.minimumOrderQty && item.minimumOrderQty > 1) {
                item.selectedQuantity = item.minimumOrderQty;
            } else {
                item.selectedQuantity = previousQuantity + 1;
            }

            item.isAddingToCart = true;
            item.cartError = undefined;

            const productId = Number(item.skuId);
            this.pmPriceDiscoveryService.addToCart(productId, item.selectedQuantity, 'DELIVERY', this.selectedEntityId)
                .subscribe({
                    next: (response) => {
                        item.isAddingToCart = false;
                        this.sharedService.successAlert(`Added "${item.itemName}" to cart`);
                        if (this.hyperpureSupplier) {
                            this.hyperpureSupplier.fetchCart();
                        }
                        this.fetchHyperpureCartData();
                    },
                    error: (error) => {
                        console.error('Failed to add HP item to cart:', error);
                        item.isAddingToCart = false;
                        item.selectedQuantity = previousQuantity;
                        const errorMessage = error?.error?.message || error?.message || 'Failed to add item to cart.';
                        this.sharedService.errorAlert(errorMessage);
                    }
                });
        }
    }

    /**
     * Updates the quantity of an HP item in the Hyperpure cart.
     */
    updateHpQuantity(item: any, change: number): void {
        if (item.isAddingToCart) return;

        const previousQuantity = item.selectedQuantity || 0;
        
        // Setup item properties required by HyperpureSupplierComponent
        item.sku_id = item.skuId || item.productId || item.product_id || item.sku_id || item.id;
        item.item_name = item.itemName || item.item_name || item.name;
        item.platform = 'hyperpure';

        // Enforce maximum order quantity
        if (change > 0 && item.maximumOrderQty && (previousQuantity + change) > item.maximumOrderQty) {
            this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
            return;
        }

        item.selectedQuantity = previousQuantity + change;
        if (item.selectedQuantity < 0) item.selectedQuantity = 0;

        // When decrementing to below minimum, remove entirely
        if (change < 0 && item.minimumOrderQty && item.selectedQuantity > 0 && item.selectedQuantity < item.minimumOrderQty) {
            item.selectedQuantity = 0;
        }

        item.isAddingToCart = true;
        item.cartError = undefined;

        const productId = Number(item.skuId || item.sku_id);
        this.pmPriceDiscoveryService.addToCart(productId, item.selectedQuantity, 'DELIVERY', this.selectedEntityId)
            .subscribe({
                next: (response) => {
                    item.isAddingToCart = false;
                    if (this.hyperpureSupplier) {
                        this.hyperpureSupplier.fetchCart();
                    }
                    this.fetchHyperpureCartData();
                },
                error: (error) => {
                    console.error('Failed to update HP cart quantity:', error);
                    item.isAddingToCart = false;
                    item.selectedQuantity = previousQuantity;
                    const errorMessage = error?.error?.message || error?.message || 'Failed to update quantity.';
                    this.sharedService.errorAlert(errorMessage);
                }
            });
    }

    /**
     * Syncs the quantities of HP Recent Purchase items with the current Hyperpure cart.
     */
    syncHpRecentPurchaseQuantities(): void {
        if (!this.hyperpureSupplier || !this.hyperpureSupplier.apiCartItems) return;
        
        // Build a map of sku_id -> quantity from the supplier's cart
        const cartMap = new Map<string, number>();
        this.hyperpureSupplier.apiCartItems.forEach((cartItem: any) => {
            cartMap.set(String(cartItem.sku_id), cartItem.quantity || cartItem.selectedQuantity || 0);
        });

        if (this.hpRecentPurchaseLiveItems) {
            this.hpRecentPurchaseLiveItems.forEach((item: any) => {
                const itemId = String(item.skuId || item.productId || item.product_id || item.sku_id || item.id);
                item.selectedQuantity = cartMap.get(itemId) || 0;
            });
        }
    }

    searchFavoriteItem(favItem: any): void {
        const name = favItem.item_name || favItem.itemName || favItem.name;
        if (name) {
            this.showFavoritesModal = false;
            this.router.navigate(['/purchase-manager/price-discovery'], {
                queryParams: { tags: this.sharedService.encryptForUrl(name) }
            });
        }
    }

    // ── Product Card Add ──

    /**
     * Utility to navigate to price discovery for a given product card.
     * @param item The product card to add to price discovery.
     */
    addItemToCart(item: ProductCard): void {
        // Navigate to price discovery with this item
        this.router.navigate(['/purchase-manager/price-discovery'], {
            queryParams: { tags: this.sharedService.encryptForUrl(item.name) }
        });
    }

    // ── Add to Search ──

    /**
     * Adds a recent searched item's name to the search tags.
     * @param item The product card to add to search.
     */
    addToSearch(item: ProductCard): void {
        const tagName = item.name;
        if (tagName && !this.searchTags.find(t => t.name.toLowerCase() === tagName.toLowerCase())) {
            this.searchTags.push({
                id: item.id || 'recent_' + Date.now(),
                name: tagName
            });
        }
    }

    /**
     * Removes a product card item from the search tags.
     */
    removeFromSearch(item: ProductCard): void {
        const tag = this.searchTags.find(t => t.name.toLowerCase() === item.name.toLowerCase());
        if (tag) {
            this.removeSearchTag(tag);
        }
    }

    /**
     * Checks if an item is already added to search tags.
     */
    isItemAdded(item: ProductCard): boolean {
        return this.searchTags.some(t => t.name.toLowerCase() === item.name.toLowerCase());
    }

    // ── Helpers ──

    /**
     * Maps recent-searched-items API response to ProductCard array.
     * @param items Raw API response items.
     * @returns Mapped ProductCard array.
     */
    private mapRecentSearchedItems(items: any[]): ProductCard[] {
        if (!items || items.length === 0) return [];

        return items.map((item: any) => {
            return {
                id: item.requestId || item.skuId || item.itemName,
                name: item.itemName || 'Unknown Item',
                packageInfo: item.unit || '',
                priceLabel: 'PRICE',
                minPrice: item.rate || 0,
                maxPrice: item.rate || 0,
                imageUrl: item.productImage || '',
                supplier: item.platform || '',
                unit: item.unit || '',
                quantity: 1
            };
        });
    }

    /**
     * Maps raw API items to ProductCard interface for consistent UI display.
     * @param items Array of raw product items.
     * @returns Array of mapped ProductCard objects.
     */
    private mapToProductCards(items: any[]): ProductCard[] {
        if (!items || items.length === 0) return [];

        return items.slice(0, 10).map((item: any) => {
            const name = item.itemName || item.item_name || item.name || 'Unknown Item';
            const unit = item.unit || item.uom || 'kg';
            const qty = item.quantity || item.qty || 1;
            const rate = item.rate || item.price || item.unitPrice || 0;
            const minRate = item.minRate || item.min_rate || rate * 0.8;
            const maxRate = item.maxRate || item.max_rate || rate;

            return {
                id: item.id || item._id || name,
                name: name,
                packageInfo: `${qty}${unit} ${item.packageType || 'Pack'}`,
                priceLabel: 'PRICE',
                minPrice: minRate,
                maxPrice: maxRate,
                imageUrl: item.imageUrl || item.image_url || '',
                supplier: item.supplierName || item.supplier_name || '',
                unit: unit,
                quantity: qty
            };
        });
    }

    /**
     * Gets the first 8 search tags to display in the UI (or all if expanded).
     */
    get visibleTags(): SearchTag[] {
        return this.showAllTags ? this.searchTags : this.searchTags.slice(0, 8);
    }

    /**
     * Gets the count of search tags that are hidden beyond the visible limit.
     */
    get hiddenTagCount(): number {
        return Math.max(0, this.searchTags.length - 8);
    }

    // ── Auto-Fill Smart Cart ──

    /**
     * Handles configuration changes from the Auto-Fill Smart Cart component.
     * @param supplierPreferences Array of supplier IDs in preferred order.
     */
    onSmartCartConfigChanged(supplierPreferences: string[]): void {
        // Config saved successfully - can be used to refresh other dependent components if needed
        console.log('Smart Cart configuration updated:', supplierPreferences);
    }
}
